import {
  noPermission,
  PermissionDeniedReasonsEnum,
  PermissionResult,
  yesPermission,
} from '../../src/helpers/permissionHelpers';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../src/store/panels/inboxes/parameters/inboxesParameters';

/*
 * Generic hook.
 */

/** Computes the bulk action permission from the inboxes parameters. */
export function useBulkActionPermission(
  isBulkActionRequired: boolean,
  isBulkActionSupported: boolean,
  inboxesParameters: InboxesParameters,
): PermissionResult {
  if (!isBulkActionRequired) {
    // We don't need to perform a bulk action, so pass the permission check.
    return yesPermission();
  }

  if (!isBulkActionSupported) {
    // The bulk action is not supported at all, so we fail the check.
    return noPermission(PermissionDeniedReasonsEnum.BULK_UNSUPPORTED);
  }

  if (inboxesParameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
    // We don't support bulk actions for drafts or sent.
    return noPermission(PermissionDeniedReasonsEnum.BULK_UNSUPPORTED);
  }

  return yesPermission();
}

/*
 * Specialized hooks.
 */

export const useConversationStatusBulkActionPermission = makeUseBulkActionPermission(true);

export const useDestroyBulkActionPermission = makeUseBulkActionPermission(false);

export const useSubscribeBulkActionPermission = makeUseBulkActionPermission(false);

export const useTagBulkActionPermission = makeUseBulkActionPermission(true);

export const useMoveBulkActionPermission = makeUseBulkActionPermission(true);

export const useAssignBulkActionPermission = makeUseBulkActionPermission(true);

export const useMergeBulkActionPermission = makeUseBulkActionPermission(false);

export const useSeenBulkActionPermission = makeUseBulkActionPermission(true);

/*
 * Helpers.
 */

function makeUseBulkActionPermission(isBulkActionSupported: boolean) {
  return (isBulkActionRequired: boolean, inboxesParameters: InboxesParameters) =>
    useBulkActionPermission(isBulkActionRequired, isBulkActionSupported, inboxesParameters);
}
